import React, { useState, useEffect } from 'react';
import { User, JobPosting, FilterState, Application, Interview } from './types';
import { api, getStoredUser, clearAuthData } from './services/api';
import { Navbar } from './components/Navbar';
import { HeroSection } from './components/HeroSection';
import { JobList } from './components/JobList';
import { JobDetailModal } from './components/JobDetailModal';
import { ApplicationModal } from './components/ApplicationModal';
import { ApplicantDashboard } from './components/ApplicantDashboard';
import { HiringDashboard } from './components/HiringDashboard';
import { AuthModal } from './components/AuthModal';
import { CreateJobModal } from './components/CreateJobModal';
import { ScheduleInterviewModal } from './components/ScheduleInterviewModal';
import { WebRTCInterviewRoom } from './components/WebRTCInterviewRoom';

export const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(getStoredUser());
  const [activeTab, setActiveTab] = useState<'jobs' | 'my-applications' | 'hiring-dashboard'>('jobs');
  
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    department: 'All',
    type: 'All',
  });

  // Selected modals state
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);
  const [applyingJob, setApplyingJob] = useState<JobPosting | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isCreateJobModalOpen, setIsCreateJobModalOpen] = useState(false);
  const [schedulingApp, setSchedulingApp] = useState<Application | null>(null);
  const [activeInterviewRoom, setActiveInterviewRoom] = useState<Interview | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await api.getJobs({
        q: filters.search,
        department: filters.department,
        type: filters.type,
      });
      setJobs(res.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleLogout = () => {
    clearAuthData();
    setUser(null);
    setActiveTab('jobs');
    showToast('Logged out successfully');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: 'var(--color-navy-modal)',
            border: '1px solid var(--color-aqua)',
            color: 'var(--text-main)',
            padding: '12px 20px',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-glow-aqua)',
            zIndex: 2000,
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* Navigation */}
      <Navbar
        user={user}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenAuthModal={() => setIsAuthModalOpen(true)}
        onOpenCreateJobModal={() => setIsCreateJobModalOpen(true)}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <main style={{ flex: 1 }}>
        {activeTab === 'jobs' && (
          <>
            <HeroSection totalJobs={jobs.length} />
            <div className="container">
              <JobList
                jobs={jobs}
                filters={filters}
                onFilterChange={setFilters}
                onSelectJob={(job) => setSelectedJob(job)}
              />
            </div>
          </>
        )}

        {activeTab === 'my-applications' && user && (
          <div className="container">
            <ApplicantDashboard
              user={user}
              onLaunchVideoRoom={(interview) => setActiveInterviewRoom(interview)}
            />
          </div>
        )}

        {activeTab === 'hiring-dashboard' && user && (
          <div className="container">
            <HiringDashboard
              user={user}
              onOpenCreateJob={() => setIsCreateJobModalOpen(true)}
              onLaunchVideoRoom={(interview) => setActiveInterviewRoom(interview)}
              onScheduleInterview={(app) => setSchedulingApp(app)}
              onJobsUpdated={fetchJobs}
            />
          </div>
        )}
      </main>

      {/* Modals */}
      <JobDetailModal
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
        onApply={(job) => setApplyingJob(job)}
      />

      <ApplicationModal
        job={applyingJob}
        user={user}
        onClose={() => setApplyingJob(null)}
        onSuccess={() => {
          showToast('Application submitted successfully!');
          if (user) setActiveTab('my-applications');
        }}
        onRequireAuth={() => setIsAuthModalOpen(true)}
      />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={(newUser) => {
          setUser(newUser);
          showToast(`Welcome, ${newUser.name}!`);
          if (newUser.role === 'hiring_team') {
            setActiveTab('hiring-dashboard');
          }
        }}
      />

      <CreateJobModal
        isOpen={isCreateJobModalOpen}
        onClose={() => setIsCreateJobModalOpen(false)}
        onSuccess={() => {
          showToast('New job posting created successfully!');
          fetchJobs();
        }}
      />

      <ScheduleInterviewModal
        application={schedulingApp}
        onClose={() => setSchedulingApp(null)}
        onSuccess={(interview) => {
          showToast(`Interview scheduled! Room ID: ${interview.room_id}`);
          if (user?.role === 'hiring_team') {
            setActiveTab('hiring-dashboard');
          }
        }}
      />

      {activeInterviewRoom && user && (
        <WebRTCInterviewRoom
          interview={activeInterviewRoom}
          user={user}
          onClose={() => setActiveInterviewRoom(null)}
        />
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>© 2026 SansCraft Network. All rights reserved.</p>
          <div className="footer-links">
            <a href="https://sanscraft.top" className="footer-link" target="_blank" rel="noreferrer">
              Main Network Portal
            </a>
            <span style={{ color: 'var(--text-dim)' }}>•</span>
            <a href="https://careers.sanscraft.top" className="footer-link">
              careers.sanscraft.top
            </a>
            <span style={{ color: 'var(--text-dim)' }}>•</span>
            <span style={{ color: 'var(--text-dim)' }}>Powered by Cloudflare Workers, D1 & WebRTC</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
